import { memone } from '@deqo/foundation';
import { invokeLater } from '../../../CodesThatsGottaGoSomewhereElse';
import { bindCommand, defineCommand } from '../../../concepts/commands/CommandDefinition';
import { Component, ComponentPolicy, ObjectManager, component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { GeomUtil } from '../../../platform/geom/GeomUtil';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { DropInfo, DropTarget } from '../../canvas/entities/DropTarget';
import { Physical, PotentialInteraction, RelativeTo, TransformHints } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Secret } from '../../canvas/entities/Secret';
import { Snappable } from '../../canvas/entities/Snappable';
import { toolEquip } from '../../canvas/tooling/ToolCommands';
import { ToolManager } from '../../canvas/tooling/ToolManager';
import { RoadmapCard } from '../paper/RoadmapCard';
import { Pingable } from '../presentation/Pingable';
import { Symbol } from '../props/Symbol';
import { trayInsert } from './TrayCommands';
import { TrayLayout } from './TrayLayout';
import type { TrayMode } from './TrayMode';
import { trayPropertyLayout } from './TrayPropertyLayout';

const drag = defineCommand(
    'trayDrag',
    null,
    (cc, [id, hint, moved]: [string, string?, boolean?]) => {

        if (hint.startsWith('item:')) {
            const objects = cc.resolve(ObjectManager);
            const tray = objects.getMaybe(id, Tray);
            if (!tray) return;

            const index = parseInt(hint.slice(5));
            const item = tray.children[index];
            if (!item) return;

            const tools = cc.resolve(ToolManager);
            tools.setSetting('cloneTool', 'targetId', item.id);
            tools.setSetting('cloneTool', 'deleteSource', tray.editing);

            invokeLater(() => cc.dispatch(toolEquip, ['cloneTool', `${moved ? 'drop' : 'plop'}:edit`]));
        }
    },
    [],
);

const mutation = defineCommand(
    'trayMutation',
    null,
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const trayDef = {
    name: 'Tray',
    implements: [Physical, Snappable, Pingable, DropTarget, Secret],
    propertyLayout: trayPropertyLayout,
    drag,
    mutation,
};

const trayComponentPolicy = ComponentPolicy.default;

export type TrayState = 'voting' | 'results';

@component(trayDef)
export class Tray extends RectangularPhysical implements DropTarget {

    @property({ type: 'string', default: 'row' })
    public flow: 'row' | 'column';

    @property({ type: 'string', default: 'none' })
    public mode: TrayMode;

    @property({ type: 'boolean', default: false })
    public editing: boolean;

    public get boundary() {
        return true;
    }

    @memoUsing('width', 'height', 'flow', 'editing', 'children')
    public get layout() {
        return TrayLayout(this);
    }

    public get shouldSnap() {
        return true;
    }

    protected get policy(): ComponentPolicy {
        return trayComponentPolicy;
    }

    public setPreferredSize() {
        const { width, height } = this.layout.base;
        if (width !== this.width) this.width = width;
        if (width !== this.height) this.height = height;
    }

    public queryDrop(mpt: Point, subjects: Iterable<Component>): DropInfo {

        if (!this.editing) {
            return null;
        }

        // Supports dropping tokens, stickies and graphics...
        for (const s of subjects) {
            if (!s.is(AutoBringToFront) && !s.is(RoadmapCard) && !s.is(Symbol)) {
                return null;
            }
        }

        const toModel = this.transform(RelativeTo.Model);
        const rpt = this.toRelative(mpt);

        if (this.layout.base.contains(rpt)) {
            if (this.layout.items.length > 0) {

                const insertSpots = [
                    ...this.layout.items.map((tm, i) => ({
                        index: i,
                        guide: this.flow === 'row'
                            ? tm.lines().at(3).translate(-5, 0)
                            : tm.lines().at(0).translate(0, -5),
                    })),
                    {
                        index: this.layout.items.length,
                        guide: this.flow === 'row'
                            ? Rect.fromMany(this.layout.items).lines().at(1).translate(5, 0)
                            : Rect.fromMany(this.layout.items).lines().at(2).translate(0, 5)
                    }
                ];

                const { index, guide } = GeomUtil.nearestTo(rpt, insertSpots, i => i.guide.mid);

                return {
                    target: this,
                    guides: [{ points: toModel.apply(guide).toArray() }],
                    command: bindCommand(trayInsert, [this.id, index, Array.from(subjects).map(s => s.id)]),
                };

            }
            else {

                const guide = this.layout.base;

                return {
                    target: this,
                    guides: [{ points: toModel.apply(guide) }],
                    command: bindCommand(trayInsert, [this.id, 0, Array.from(subjects).map(s => s.id)]),
                };
            }

        }
        else {
            return null;
        }

    }

    @memone()
    public queryTransformHints() {
        return TransformHints({
            ...TransformHints.off,
            translate: true,
        });
    }

    public testInteraction({ type, mpt }: PotentialInteraction): [boolean, string] {
        const { base, items } = this.layout;
        const rpt = this.toRelative(mpt);
        if (type === 'drag' && base.contains(rpt)) {
            for (const tm of items) {
                if (tm.contains(rpt)) {
                    return [true, `item:${items.indexOf(tm)}`];
                }
            }
        }
        if (type === 'select') {
            return [true, 'default'];
        }
        return [false, 'none'];
    }

    protected setTransformationResult(x: number, y: number, r: number, w: number, h: number): void {
        const newBounds = new Rect(x, y, w, h);
        const newCenter = newBounds.center();

        w = this.layout.base.width;
        h = this.layout.base.height;
        x = newCenter.x - (w / 2);
        y = newCenter.y - (h / 2);
        r = 0;

        super.setTransformationResult(x, y, r, w, h);
    }
}

